import {
  CheckersPiece,
  CheckersPieceGrid,
  CheckersGameState,
  CheckersGameStatus,
  CheckersMove,
  CheckersMoveResult,
  PlayerColor,
  Position,
  isDarkSquare,
  isValidPosition,
  positionToNotation,
  positionsEqual,
} from './types';

/**
 * CheckersEngine - Core game logic for American Checkers
 *
 * Manages game state, validates moves, and enforces all American Checkers rules.
 * This is the foundation class; move validation, AI integration, and additional
 * methods will be added in subsequent tasks.
 */
export class CheckersEngine {
  private gameState: CheckersGameState;

  constructor() {
    this.gameState = this.createInitialGameState();
    this.initializeGame();
  }

  /**
   * Creates the initial empty game state structure.
   */
  private createInitialGameState(): CheckersGameState {
    return {
      board: [],
      currentPlayer: 'dark',
      moveHistory: [],
      capturedPieces: { dark: 0, light: 0 },
      gameStatus: { type: 'active' },
      movesSinceCapture: 0,
      movesSinceRegularMove: 0,
      positionHistory: [],
      multiJumpInProgress: false,
      multiJumpPiece: null,
      kingOnlyMoveCount: 0,
    };
  }

  /**
   * Initializes the game board with the standard American Checkers starting position.
   *
   * - 8x8 grid
   * - 12 dark pieces on dark squares of rows 0-2
   * - 12 light pieces on dark squares of rows 5-7
   * - Empty dark squares on rows 3-4
   * - All pieces are type 'regular'
   * - Dark squares: (row + col) % 2 === 1
   */
  initializeGame(): void {
    const board: CheckersPieceGrid = [];

    for (let row = 0; row < 8; row++) {
      const boardRow: (CheckersPiece | null)[] = [];
      for (let col = 0; col < 8; col++) {
        if (isDarkSquare({ row, col })) {
          if (row >= 0 && row <= 2) {
            // Dark pieces on rows 0-2
            boardRow.push({
              color: 'dark',
              type: 'regular',
              position: { row, col },
            });
          } else if (row >= 5 && row <= 7) {
            // Light pieces on rows 5-7
            boardRow.push({
              color: 'light',
              type: 'regular',
              position: { row, col },
            });
          } else {
            // Empty dark squares on rows 3-4
            boardRow.push(null);
          }
        } else {
          // Light squares are always null (not playable)
          boardRow.push(null);
        }
      }
      board.push(boardRow);
    }

    this.gameState.board = board;
    this.gameState.currentPlayer = 'dark';
    this.gameState.moveHistory = [];
    this.gameState.capturedPieces = { dark: 0, light: 0 };
    this.gameState.gameStatus = { type: 'active' };
    this.gameState.movesSinceCapture = 0;
    this.gameState.movesSinceRegularMove = 0;
    this.gameState.positionHistory = [];
    this.gameState.multiJumpInProgress = false;
    this.gameState.multiJumpPiece = null;
    this.gameState.kingOnlyMoveCount = 0;
    this.stateHistory = [];
  }

  /**
   * Returns the current board state.
   */
  getBoard(): CheckersPieceGrid {
    return this.gameState.board;
  }

  /**
   * Returns the complete game state.
   */
  getGameState(): CheckersGameState {
    return this.gameState;
  }

  /**
   * Returns the current player whose turn it is.
   */
  getCurrentPlayer(): PlayerColor {
    return this.gameState.currentPlayer;
  }

  /**
   * Returns the current game status.
   */
  getGameStatus(): CheckersGameStatus {
    return this.gameState.gameStatus;
  }

  /**
   * Returns all legal move destinations for the piece at the given position.
   *
   * Enforces mandatory capture: when any piece of the current player has a
   * jump move available, only jump moves are returned. If the piece at the
   * given position has no jumps but another piece does, an empty array is
   * returned (the player must use a piece that can jump).
   *
   * When no jumps are available for any piece, simple moves are returned.
   */
  getLegalMoves(position: Position): Position[] {
    const board = this.gameState.board;
    const piece = board[position.row]?.[position.col];

    if (!piece) {
      return [];
    }

    const currentPlayer = this.gameState.currentPlayer;

    // Check if any piece of the current player has jump moves
    if (this.hasJumpMoves(currentPlayer)) {
      // Mandatory capture: return only jump moves for this piece
      return this.getJumpMoves(position);
    }

    // No jumps available for any piece — return simple moves
    return this.getSimpleMoves(position);
  }

  /**
   * Checks if any piece of the given player has at least one jump move available.
   */
  hasJumpMoves(player: PlayerColor): boolean {
    const board = this.gameState.board;

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const piece = board[row][col];
        if (piece && piece.color === player) {
          const jumps = this.getJumpMoves({ row, col });
          if (jumps.length > 0) {
            return true;
          }
        }
      }
    }

    return false;
  }

  /**
   * Returns all legal moves for all pieces of the given player, respecting
   * mandatory capture. Each move is returned as a CheckersMove object.
   *
   * When jumps are available, only jump moves are included.
   * When no jumps are available, simple moves are included.
   */
  getAllLegalMoves(player: PlayerColor): CheckersMove[] {
    const board = this.gameState.board;
    const moves: CheckersMove[] = [];
    const jumpsAvailable = this.hasJumpMoves(player);

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const piece = board[row][col];
        if (!piece || piece.color !== player) {
          continue;
        }

        const from: Position = { row, col };
        const destinations = jumpsAvailable
          ? this.getJumpMoves(from)
          : this.getSimpleMoves(from);

        for (const to of destinations) {
          const isJump = Math.abs(to.row - from.row) === 2;
          const capturedPieces: Position[] = [];

          if (isJump) {
            capturedPieces.push(this.getJumpedPosition(from, to));
          }

          // Build notation
          const fromNotation = positionToNotation(from);
          const toNotation = positionToNotation(to);
          const separator = isJump ? 'x' : '-';
          const notation = `${fromNotation}${separator}${toNotation}`;

          // Check if this move would result in promotion
          const isPromotion =
            piece.type === 'regular' &&
            ((piece.color === 'dark' && to.row === 7) ||
              (piece.color === 'light' && to.row === 0));

          moves.push({
            from,
            to,
            piece: { ...piece },
            isJump,
            capturedPieces: capturedPieces.length > 0 ? capturedPieces : undefined,
            isPromotion,
            notation,
            timestamp: Date.now(),
          });
        }
      }
    }

    return moves;
  }

  /**
   * Returns all valid jump destinations for the piece at the given position.
   *
   * A jump move requires:
   * - An opponent's piece on an adjacent diagonal square
   * - An empty square beyond the opponent's piece (landing square)
   * - The landing square must be within bounds and on a dark square
   * - Regular pieces can only jump forward
   * - Cannot jump over own pieces
   */
  getJumpMoves(position: Position): Position[] {
    const board = this.gameState.board;
    const piece = board[position.row]?.[position.col];

    // No piece at position
    if (!piece) {
      return [];
    }

    const moves: Position[] = [];

    // Determine forward direction based on piece color
    const forwardDirections: number[] = piece.color === 'dark' ? [1] : [-1];

    // Regular pieces jump forward only; kings will add backward later
    const rowDirections = piece.type === 'king' ? [1, -1] : forwardDirections;

    // Check both diagonal columns (left and right)
    const colDirections = [-1, 1];

    for (const rowDir of rowDirections) {
      for (const colDir of colDirections) {
        const adjacent: Position = {
          row: position.row + rowDir,
          col: position.col + colDir,
        };

        const landing: Position = {
          row: position.row + rowDir * 2,
          col: position.col + colDir * 2,
        };

        // Landing must be within board bounds
        if (!isValidPosition(landing)) {
          continue;
        }

        // Adjacent square must be within bounds
        if (!isValidPosition(adjacent)) {
          continue;
        }

        // Adjacent square must contain an opponent's piece
        const adjacentPiece = board[adjacent.row][adjacent.col];
        if (!adjacentPiece || adjacentPiece.color === piece.color) {
          continue;
        }

        // Landing square must be empty
        if (board[landing.row][landing.col] !== null) {
          continue;
        }

        moves.push(landing);
      }
    }

    return moves;
  }

  /**
   * Returns the position of the piece being jumped over (the midpoint
   * between from and to positions).
   *
   * This is useful for identifying which piece was captured during a jump.
   */
  getJumpedPosition(from: Position, to: Position): Position {
    return {
      row: (from.row + to.row) / 2,
      col: (from.col + to.col) / 2,
    };
  }

  /**
   * Returns all simple (non-jump) move destinations for the piece at the
   * given position.
   *
   * Simple moves are one square diagonally forward for regular pieces.
   * Kings move in all four diagonal directions.
   * The destination must be within bounds, on a dark square, and unoccupied.
   */
  getSimpleMoves(position: Position): Position[] {
    const board = this.gameState.board;
    const piece = board[position.row]?.[position.col];

    // No piece at position
    if (!piece) {
      return [];
    }

    const moves: Position[] = [];

    // Determine forward direction based on piece color
    // Dark pieces move toward row 7 (increasing row)
    // Light pieces move toward row 0 (decreasing row)
    const forwardDirections: number[] = piece.color === 'dark' ? [1] : [-1];

    // Regular pieces move forward only; kings move in all four diagonal directions
    const rowDirections = piece.type === 'king' ? [1, -1] : forwardDirections;

    // Check both diagonal columns (left and right)
    const colDirections = [-1, 1];

    for (const rowDir of rowDirections) {
      for (const colDir of colDirections) {
        const dest: Position = {
          row: position.row + rowDir,
          col: position.col + colDir,
        };

        // Must be within board bounds
        if (!isValidPosition(dest)) {
          continue;
        }

        // Must be a dark square
        if (!isDarkSquare(dest)) {
          continue;
        }

        // Must be unoccupied
        if (board[dest.row][dest.col] !== null) {
          continue;
        }

        moves.push(dest);
      }
    }

    return moves;
  }

  // ============================================================================
  // Multi-Jump State (Task 6.1)
  // ============================================================================

  /**
   * Returns whether a multi-jump sequence is currently in progress.
   */
  isMultiJumpInProgress(): boolean {
    return this.gameState.multiJumpInProgress;
  }

  /**
   * Returns the position of the piece currently in a multi-jump sequence,
   * or null if no multi-jump is in progress.
   */
  getMultiJumpPiece(): Position | null {
    return this.gameState.multiJumpPiece;
  }

  // ============================================================================
  // Move History & Captured Pieces (Task 9.1)
  // ============================================================================

  /**
   * Returns the move history.
   */
  getMoveHistory(): CheckersMove[] {
    return this.gameState.moveHistory;
  }

  /**
   * Returns the count of captured pieces for each player.
   */
  getCapturedPieces(): { dark: number; light: number } {
    return this.gameState.capturedPieces;
  }

  // ============================================================================
  // Board Serialization (for position history / repetition detection)
  // ============================================================================

  /**
   * Creates a string representation of the current board position including
   * piece positions, colors, types, and current player. Used for repetition
   * detection in draw conditions.
   */
  private serializeBoardPosition(): string {
    const board = this.gameState.board;
    const parts: string[] = [];

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const piece = board[row][col];
        if (piece) {
          const colorChar = piece.color === 'dark' ? 'd' : 'l';
          const typeChar = piece.type === 'king' ? 'k' : 'r';
          parts.push(`${row}${col}${colorChar}${typeChar}`);
        }
      }
    }

    return `${this.gameState.currentPlayer}:${parts.join(',')}`;
  }

  // ============================================================================
  // Kinging Logic (Task 7.1)
  // ============================================================================

  /**
   * Checks if a piece at the given position should be promoted to king.
   * Dark pieces are promoted at row 7, light pieces at row 0.
   */
  private shouldPromote(piece: CheckersPiece, position: Position): boolean {
    if (piece.type === 'king') return false;
    if (piece.color === 'dark' && position.row === 7) return true;
    if (piece.color === 'light' && position.row === 0) return true;
    return false;
  }

  /**
   * Promotes the piece at the given position to a king.
   */
  private promotePiece(position: Position): void {
    const piece = this.gameState.board[position.row][position.col];
    if (piece && piece.type === 'regular') {
      piece.type = 'king';
    }
  }

  // ============================================================================
  // Multi-Jump Helpers (Task 6.1)
  // ============================================================================

  /**
   * Returns jump moves from a position, excluding positions of already-captured
   * pieces in the current multi-jump sequence. This prevents jumping over the
   * same opponent piece twice.
   */
  private getJumpMovesExcluding(position: Position, alreadyCaptured: Position[]): Position[] {
    const board = this.gameState.board;
    const piece = board[position.row]?.[position.col];

    if (!piece) return [];

    const moves: Position[] = [];
    const forwardDirections: number[] = piece.color === 'dark' ? [1] : [-1];
    const rowDirections = piece.type === 'king' ? [1, -1] : forwardDirections;
    const colDirections = [-1, 1];

    for (const rowDir of rowDirections) {
      for (const colDir of colDirections) {
        const adjacent: Position = {
          row: position.row + rowDir,
          col: position.col + colDir,
        };
        const landing: Position = {
          row: position.row + rowDir * 2,
          col: position.col + colDir * 2,
        };

        if (!isValidPosition(landing) || !isValidPosition(adjacent)) continue;

        const adjacentPiece = board[adjacent.row][adjacent.col];
        if (!adjacentPiece || adjacentPiece.color === piece.color) continue;

        // Check if this piece was already captured in this multi-jump sequence
        if (alreadyCaptured.some(cap => positionsEqual(cap, adjacent))) continue;

        // Landing square must be empty
        if (board[landing.row][landing.col] !== null) continue;

        moves.push(landing);
      }
    }

    return moves;
  }

  // ============================================================================
  // makeMove - Core Move Execution (Task 9.1)
  // ============================================================================

  /**
   * Executes a move from one position to another with full validation.
   *
   * This is the central method that ties together:
   * - Move validation (piece exists, belongs to current player, destination is legal)
   * - Game status check (reject if game is not active)
   * - Multi-jump enforcement (only allow moves from multi-jump piece)
   * - Move execution (move piece, remove captured piece if jump)
   * - Kinging (promote if reaching king row)
   * - Multi-jump continuation (check for additional jumps after a jump)
   * - Turn switching (after completed move or completed multi-jump)
   * - Move history recording
   * - Draw tracking counter updates
   * - Position history recording for repetition detection
   */
  makeMove(from: Position, to: Position): CheckersMoveResult {
    const board = this.gameState.board;
    const currentPlayer = this.gameState.currentPlayer;

    // 1. Game must be active
    if (this.gameState.gameStatus.type !== 'active') {
      return {
        success: false,
        error: 'Game is not active',
        gameStatus: this.gameState.gameStatus,
        multiJumpContinuation: false,
      };
    }

    // 2. Validate piece exists at 'from'
    const piece = board[from.row]?.[from.col];
    if (!piece) {
      return {
        success: false,
        error: 'No piece at selected position',
        gameStatus: this.gameState.gameStatus,
        multiJumpContinuation: this.gameState.multiJumpInProgress,
      };
    }

    // 3. Piece must belong to current player
    if (piece.color !== currentPlayer) {
      return {
        success: false,
        error: 'Cannot move opponent\'s piece',
        gameStatus: this.gameState.gameStatus,
        multiJumpContinuation: this.gameState.multiJumpInProgress,
      };
    }

    // 4. If multi-jump in progress, only allow moves from the multi-jump piece
    if (this.gameState.multiJumpInProgress && this.gameState.multiJumpPiece) {
      if (!positionsEqual(from, this.gameState.multiJumpPiece)) {
        return {
          success: false,
          error: 'You must continue jumping with the same piece',
          gameStatus: this.gameState.gameStatus,
          multiJumpContinuation: true,
        };
      }
    }

    // 5. Validate destination is legal
    const isJump = Math.abs(to.row - from.row) === 2 && Math.abs(to.col - from.col) === 2;
    const isSimple = Math.abs(to.row - from.row) === 1 && Math.abs(to.col - from.col) === 1;

    if (!isJump && !isSimple) {
      return {
        success: false,
        error: 'Illegal move',
        gameStatus: this.gameState.gameStatus,
        multiJumpContinuation: this.gameState.multiJumpInProgress,
      };
    }

    // During multi-jump, only jumps are allowed
    if (this.gameState.multiJumpInProgress && !isJump) {
      return {
        success: false,
        error: 'You must continue jumping',
        gameStatus: this.gameState.gameStatus,
        multiJumpContinuation: true,
      };
    }

    // Check if destination is in the legal moves for this piece
    let legalMoves: Position[];
    if (this.gameState.multiJumpInProgress) {
      // During multi-jump, get jump moves excluding already-captured pieces
      // We need to track captured pieces from the ongoing multi-jump
      legalMoves = this.getJumpMoves(from);
    } else {
      legalMoves = this.getLegalMoves(from);
    }

    if (!legalMoves.some(m => positionsEqual(m, to))) {
      // Check if it's a mandatory capture violation
      if (isSimple && this.hasJumpMoves(currentPlayer)) {
        return {
          success: false,
          error: 'A capture is available — you must jump',
          gameStatus: this.gameState.gameStatus,
          multiJumpContinuation: false,
        };
      }
      return {
        success: false,
        error: 'Illegal move',
        gameStatus: this.gameState.gameStatus,
        multiJumpContinuation: this.gameState.multiJumpInProgress,
      };
    }

    // ---- Move is valid, execute it ----

    // Save state snapshot before executing the move (for undo support)
    if (!this.gameState.multiJumpInProgress) {
      this.pushStateSnapshot();
    }

    // Track whether this is part of an ongoing multi-jump for history recording
    const isMultiJumpContinuation = this.gameState.multiJumpInProgress;

    // Capture the piece snapshot before moving
    const pieceSnapshot: CheckersPiece = { ...piece, position: { ...piece.position } };

    // 6. Execute the move: move piece
    board[to.row][to.col] = piece;
    board[from.row][from.col] = null;
    piece.position = { row: to.row, col: to.col };

    // 7. If jump, remove captured piece
    let capturedPosition: Position | undefined;
    if (isJump) {
      capturedPosition = this.getJumpedPosition(from, to);
      const capturedPiece = board[capturedPosition.row][capturedPosition.col];
      board[capturedPosition.row][capturedPosition.col] = null;

      // Update captured pieces count
      if (capturedPiece) {
        this.gameState.capturedPieces[capturedPiece.color]++;
      }

      // Reset capture counter
      this.gameState.movesSinceCapture = 0;
    }

    // 8. Check for kinging
    let promoted = false;
    if (this.shouldPromote(piece, to)) {
      this.promotePiece(to);
      promoted = true;
    }

    // 9. Determine if multi-jump continues
    let multiJumpContinuation = false;
    let continuationMoves: Position[] = [];

    if (isJump && !promoted) {
      // Check for additional jumps from the landing position
      continuationMoves = this.getJumpMoves(to);
      if (continuationMoves.length > 0) {
        multiJumpContinuation = true;
      }
    }

    // 10. Build or update the move record
    if (isMultiJumpContinuation) {
      // Append to the existing multi-jump move in history
      const lastMove = this.gameState.moveHistory[this.gameState.moveHistory.length - 1];
      if (lastMove && lastMove.jumpSequence) {
        lastMove.jumpSequence.push(to);
        lastMove.to = to;
        if (capturedPosition) {
          if (!lastMove.capturedPieces) lastMove.capturedPieces = [];
          lastMove.capturedPieces.push(capturedPosition);
        }
        lastMove.isPromotion = promoted;
        // Update notation: multi-jump notation like "11x18x25"
        const toNotation = positionToNotation(to);
        lastMove.notation += `x${toNotation}`;
        lastMove.timestamp = Date.now();
      }
    } else {
      // Create a new move record
      const fromNotation = positionToNotation(from);
      const toNotation = positionToNotation(to);
      const separator = isJump ? 'x' : '-';
      const notation = `${fromNotation}${separator}${toNotation}`;

      const move: CheckersMove = {
        from,
        to,
        piece: pieceSnapshot,
        isJump,
        jumpSequence: isJump ? [from, to] : undefined,
        capturedPieces: capturedPosition ? [capturedPosition] : undefined,
        isPromotion: promoted,
        notation,
        timestamp: Date.now(),
      };

      this.gameState.moveHistory.push(move);
    }

    // 11. Update multi-jump state
    if (multiJumpContinuation) {
      this.gameState.multiJumpInProgress = true;
      this.gameState.multiJumpPiece = { row: to.row, col: to.col };
    } else {
      // Multi-jump ended or was not a jump — end turn
      this.gameState.multiJumpInProgress = false;
      this.gameState.multiJumpPiece = null;

      // 12. Update draw tracking counters
      if (!isJump) {
        this.gameState.movesSinceCapture++;
      }

      // Track moves since a regular (non-king) piece moved
      if (pieceSnapshot.type === 'regular') {
        this.gameState.movesSinceRegularMove = 0;
      } else {
        this.gameState.movesSinceRegularMove++;
      }

      // Track king-only move count
      const hasRegularPieces = this.boardHasRegularPieces();
      if (!hasRegularPieces) {
        this.gameState.kingOnlyMoveCount++;
      } else {
        this.gameState.kingOnlyMoveCount = 0;
      }

      // 13. Record position in history for repetition detection
      // Switch player first so the serialized position includes the next player
      this.gameState.currentPlayer = currentPlayer === 'dark' ? 'light' : 'dark';

      const positionKey = this.serializeBoardPosition();
      this.gameState.positionHistory.push(positionKey);
    }

    // 14. Check win/draw conditions (only when multi-jump is not in progress)
    if (!multiJumpContinuation) {
      const winStatus = this.checkWinCondition();
      if (winStatus.type !== 'active') {
        this.gameState.gameStatus = winStatus;
      } else {
        const drawStatus = this.checkDrawConditions();
        if (drawStatus.type !== 'active') {
          this.gameState.gameStatus = drawStatus;
        }
      }
    }

    // Get the latest move record for the result
    const latestMove = this.gameState.moveHistory[this.gameState.moveHistory.length - 1];

    return {
      success: true,
      move: latestMove,
      gameStatus: this.gameState.gameStatus,
      multiJumpContinuation,
      continuationMoves: multiJumpContinuation ? continuationMoves : undefined,
    };
  }

  // ============================================================================
  // Win/Draw Condition Detection (Task 10.1, 10.2)
  // ============================================================================

  /**
   * Checks if the game is over (any non-active status).
   */
  isGameOver(): boolean {
    return this.gameState.gameStatus.type !== 'active';
  }

  /**
   * Checks for win conditions:
   * 1. A player has captured all 12 opponent pieces (capture-all)
   * 2. The current player has no legal moves (no-moves → opponent wins)
   *
   * Returns the updated game status. If no win condition is met, returns { type: 'active' }.
   */
  checkWinCondition(): CheckersGameStatus {
    const board = this.gameState.board;
    const currentPlayer = this.gameState.currentPlayer;
    const opponent: PlayerColor = currentPlayer === 'dark' ? 'light' : 'dark';

    // Count pieces for each player
    let darkCount = 0;
    let lightCount = 0;
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const piece = board[row][col];
        if (piece) {
          if (piece.color === 'dark') darkCount++;
          else lightCount++;
        }
      }
    }

    // Win by capturing all opponent pieces
    if (darkCount === 0) {
      return { type: 'win', winner: 'light', reason: 'capture-all' };
    }
    if (lightCount === 0) {
      return { type: 'win', winner: 'dark', reason: 'capture-all' };
    }

    // Win by opponent having no legal moves (all pieces blocked)
    const allMoves = this.getAllLegalMoves(currentPlayer);
    if (allMoves.length === 0) {
      return { type: 'win', winner: opponent, reason: 'no-moves' };
    }

    return { type: 'active' };
  }

  /**
   * Checks for draw conditions:
   * 1. 40-move draw: 40 consecutive moves with no captures AND no regular piece movements
   * 2. Repetition: same board position occurs three times with same player to move
   * 3. Insufficient progress: both players have only one king each and kingOnlyMoveCount >= 20
   *
   * Returns the updated game status. If no draw condition is met, returns { type: 'active' }.
   */
  checkDrawConditions(): CheckersGameStatus {
    // 1. 40-move draw rule: both counters must reach 40
    if (this.gameState.movesSinceCapture >= 40 && this.gameState.movesSinceRegularMove >= 40) {
      return { type: 'draw', reason: '40-move' };
    }

    // 2. Repetition: same position occurs 3 times
    if (this.gameState.positionHistory.length > 0) {
      const lastPosition = this.gameState.positionHistory[this.gameState.positionHistory.length - 1];
      let count = 0;
      for (const pos of this.gameState.positionHistory) {
        if (pos === lastPosition) {
          count++;
        }
      }
      if (count >= 3) {
        return { type: 'draw', reason: 'repetition' };
      }
    }

    // 3. Insufficient progress: both players have exactly 1 king each, no other pieces,
    //    and kingOnlyMoveCount >= 20
    const board = this.gameState.board;
    let darkKings = 0;
    let lightKings = 0;
    let totalPieces = 0;
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const piece = board[row][col];
        if (piece) {
          totalPieces++;
          if (piece.color === 'dark' && piece.type === 'king') darkKings++;
          if (piece.color === 'light' && piece.type === 'king') lightKings++;
        }
      }
    }

    if (totalPieces === 2 && darkKings === 1 && lightKings === 1 && this.gameState.kingOnlyMoveCount >= 20) {
      return { type: 'draw', reason: 'insufficient-progress' };
    }

    return { type: 'active' };
  }

  /**
   * Checks if the board has any regular (non-king) pieces.
   */
  private boardHasRegularPieces(): boolean {
    const board = this.gameState.board;
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const piece = board[row][col];
        if (piece && piece.type === 'regular') {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Sets the game state directly. Used for state restoration.
   */
  setGameState(state: CheckersGameState): void {
    this.gameState = state;
  }

  // ============================================================================
  // Serialization / Deserialization (Task 11.1)
  // ============================================================================

  /**
   * Serializes the complete game state to a JSON string.
   * Includes board positions, piece types, current turn, move history,
   * captured pieces, game status, and draw tracking counters.
   */
  serialize(): string {
    return JSON.stringify(this.gameState);
  }

  /**
   * Deserializes a JSON string back into a CheckersGameState.
   * Validates the structure and returns a descriptive error for invalid input.
   */
  deserialize(json: string): CheckersGameState | { error: string } {
    let parsed: unknown;
    try {
      parsed = JSON.parse(json);
    } catch {
      return { error: 'Invalid JSON: unable to parse input string' };
    }

    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return { error: 'Invalid game state: expected a JSON object' };
    }

    const obj = parsed as Record<string, unknown>;

    // Validate board
    if (!Array.isArray(obj.board)) {
      return { error: 'Invalid game state: missing or invalid board' };
    }
    if (obj.board.length !== 8) {
      return { error: 'Invalid game state: board must have 8 rows' };
    }
    for (let r = 0; r < 8; r++) {
      if (!Array.isArray(obj.board[r]) || (obj.board[r] as unknown[]).length !== 8) {
        return { error: `Invalid game state: board row ${r} must have 8 columns` };
      }
    }

    // Validate currentPlayer
    if (obj.currentPlayer !== 'dark' && obj.currentPlayer !== 'light') {
      return { error: 'Invalid game state: currentPlayer must be "dark" or "light"' };
    }

    // Validate moveHistory
    if (!Array.isArray(obj.moveHistory)) {
      return { error: 'Invalid game state: missing or invalid moveHistory' };
    }

    // Validate capturedPieces
    if (
      typeof obj.capturedPieces !== 'object' ||
      obj.capturedPieces === null ||
      typeof (obj.capturedPieces as Record<string, unknown>).dark !== 'number' ||
      typeof (obj.capturedPieces as Record<string, unknown>).light !== 'number'
    ) {
      return { error: 'Invalid game state: missing or invalid capturedPieces' };
    }

    // Validate gameStatus
    if (typeof obj.gameStatus !== 'object' || obj.gameStatus === null) {
      return { error: 'Invalid game state: missing or invalid gameStatus' };
    }
    const statusType = (obj.gameStatus as Record<string, unknown>).type;
    if (!['active', 'win', 'draw', 'resignation', 'draw-agreement'].includes(statusType as string)) {
      return { error: 'Invalid game state: invalid gameStatus type' };
    }

    // Validate numeric counters
    if (typeof obj.movesSinceCapture !== 'number') {
      return { error: 'Invalid game state: missing or invalid movesSinceCapture' };
    }
    if (typeof obj.movesSinceRegularMove !== 'number') {
      return { error: 'Invalid game state: missing or invalid movesSinceRegularMove' };
    }
    if (typeof obj.kingOnlyMoveCount !== 'number') {
      return { error: 'Invalid game state: missing or invalid kingOnlyMoveCount' };
    }

    // Validate positionHistory
    if (!Array.isArray(obj.positionHistory)) {
      return { error: 'Invalid game state: missing or invalid positionHistory' };
    }

    // Validate multiJump fields
    if (typeof obj.multiJumpInProgress !== 'boolean') {
      return { error: 'Invalid game state: missing or invalid multiJumpInProgress' };
    }

    // multiJumpPiece can be null or a Position object
    if (obj.multiJumpPiece !== null && typeof obj.multiJumpPiece !== 'object') {
      return { error: 'Invalid game state: invalid multiJumpPiece' };
    }

    return parsed as CheckersGameState;
  }

  // ============================================================================
  // Undo Move (Task 12.1)
  // ============================================================================

  private stateHistory: string[] = [];

  /**
   * Saves the current game state to the history stack.
   * Called before each move execution in makeMove.
   */
  private pushStateSnapshot(): void {
    this.stateHistory.push(this.serialize());
  }

  /**
   * Undoes the last move(s). In single-player mode, undoes both the player's
   * last move and the AI's response (count defaults to 1, which undoes 1 pair).
   *
   * @param count Number of move pairs to undo (default 1). Each undo reverts
   *              one state snapshot from the history stack.
   * @returns true if undo was successful, false if no moves to undo.
   */
  undoMove(count: number = 1): boolean {
    if (count < 1 || this.stateHistory.length === 0) {
      return false;
    }

    const stepsToUndo = Math.min(count, this.stateHistory.length);
    let restoredJson: string | undefined;

    for (let i = 0; i < stepsToUndo; i++) {
      restoredJson = this.stateHistory.pop();
    }

    if (!restoredJson) {
      return false;
    }

    const restored = this.deserialize(restoredJson);
    if ('error' in restored) {
      return false;
    }

    this.gameState = restored;
    return true;
  }
}
